import React, { useState, useEffect } from 'react';
import { FilterConfig } from '@/types/ui';
import UIInput from '@/components/shared/UIInput';
import UISelect from '@/components/shared/UISelect';
import UIButton from '@/components/shared/UIButton';
import './PropertiesSearchForm.scss';

interface PropertiesSearchFormProps {
  filtersConfig: FilterConfig[];
  initialValues: { [key: string]: string };
  onSearch: (searchData: { [key: string]: string }) => void;
  isLoading?: boolean;
  error?: string;
}

const PropertiesSearchForm: React.FC<PropertiesSearchFormProps> = ({
  filtersConfig,
  initialValues,
  onSearch,
  isLoading = false,
  error,
}) => {
  const [formState, setFormState] = useState(initialValues);

  useEffect(() => {
    setFormState(initialValues);
  }, [initialValues]);

  const handleInputChange = (key: string, value: string) => {
    setFormState(prevState => ({ ...prevState, [key]: value }));
  };

  const handleSearch = () => {
    onSearch(formState);
  };

  const renderFilter = (filter: FilterConfig) => {
    // Виносимо key за межі commonProps
    const { key, ...restFilterProps } = filter;

    const commonProps = {
      value: formState[key] || '',
      disabled: isLoading,
    };

    switch (filter.type) {
      case 'search':
        return (
          <div key={key} className="PropertiesSearchForm__control-wrapper PropertiesSearchForm__control-wrapper--input">
            <UIInput
              {...commonProps}
              onChange={(e) => handleInputChange(key, e.target.value)}
              placeholder={filter.placeholder}
              className="PropertiesSearchForm__input"
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              fullWidth
            />
          </div>
        );
      case 'select':
        return (
          <div key={key} className="PropertiesSearchForm__control-wrapper PropertiesSearchForm__control-wrapper--select">
            <UISelect
              {...commonProps}
              options={filter.options || []}
              onChange={(e) => handleInputChange(key, (e.target as HTMLSelectElement).value)}
              placeholder={filter.label}
              className="PropertiesSearchForm__select"
              fullWidth
            />
          </div>
        );
      default:
        return null;
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return <div className="PropertiesSearchForm__message">Loading filters...</div>;
    }
    if (error) {
      return <div className="PropertiesSearchForm__message PropertiesSearchForm__message--error">Could not load filters: {error}</div>;
    }
    if (!filtersConfig || filtersConfig.length === 0) {
      return <div className="PropertiesSearchForm__message">No filters available for this section.</div>;
    }
    return (
      <>
        <div className="PropertiesSearchForm__controls">
          {filtersConfig.map(renderFilter)}
        </div>
        <div className="PropertiesSearchForm__actions">
          <UIButton onClick={handleSearch} variant="primary" disabled={isLoading}>Search</UIButton>
        </div>
      </>
    );
  };

  return (
    <div className="PropertiesSearchForm">
      {renderContent()}
    </div>
  );
};

export default PropertiesSearchForm;
